import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react';

const GuideModal = ({ 
  isOpen, 
  onClose, 
  selectedEquipment,
  currentEquipmentConfig
}) => {
  const [iframeLoaded, setIframeLoaded] = React.useState(false);
  const [iframeError, setIframeError] = React.useState(false);
  const [currentIframeSrc, setCurrentIframeSrc] = React.useState('');
  const [loadingTimeoutId, setLoadingTimeoutId] = React.useState(null);

  // 利用可能なガイドページのリスト
  const AVAILABLE_GUIDES = ['Kitchen', 'Bath', 'Basin', 'Floorplan', 'Toilet'];

  // モーダルが開かれた時にiframeを読み込む
  React.useEffect(() => {
    if (isOpen && !iframeLoaded) {
      // 選択された設備のガイドページ名を生成
      const guideName = selectedEquipment.charAt(0).toUpperCase() + selectedEquipment.slice(1);
      
      // ガイドページが利用可能かチェック
      if (AVAILABLE_GUIDES.includes(guideName)) {
        // iframeの初期設定
        const targetUrl = `https://www.himawarin-lab.com/guide/${guideName}`;
        setCurrentIframeSrc(targetUrl);
        setIframeError(false);
        setIframeLoaded(true);
        
        // 10秒後にエラー表示（読み込みが遅い、または無効なURLの場合）
        const timeoutId = setTimeout(() => {
          setIframeError(true);
          setCurrentIframeSrc('');
        }, 10000);
        
        setLoadingTimeoutId(timeoutId);
      } else {
        // ガイドページが存在しない場合は即座にエラー表示
        setIframeError(true);
        setCurrentIframeSrc('');
        setIframeLoaded(true);
      }
    }
  }, [isOpen, selectedEquipment, iframeLoaded]);

  // モーダルが閉じられた時にiframe状態をリセット
  React.useEffect(() => {
    if (!isOpen) {
      setIframeLoaded(false);
      setIframeError(false);
      setCurrentIframeSrc('');
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        setLoadingTimeoutId(null);
      }
    }
  }, [isOpen, loadingTimeoutId]);
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" h="90vh">
        <ModalHeader>選び方ガイド</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} h="calc(90vh - 120px)">
          <Box height="100%">
            <Box position="relative" height="100%" width="100%">
              {!iframeLoaded ? (
                <Flex
                  width="100%"
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <Heading as="h3" size="lg" mb={4} textAlign="center" color="gray.600">
                    選び方ガイド
                  </Heading>
                  <Text fontSize="md" textAlign="center" color="gray.500">
                    ガイドを読み込み中...
                  </Text>
                </Flex>
              ) : !iframeError && currentIframeSrc ? (
                <iframe 
                  src={currentIframeSrc}
                  width="100%" 
                  height="100%"
                  onLoad={(e) => {
                    // 読み込み成功時：タイムアウトをクリアしてエラー状態をリセット
                    if (loadingTimeoutId) {
                      clearTimeout(loadingTimeoutId);
                      setLoadingTimeoutId(null);
                    }
                    
                    try {
                      // iframeの内容をチェック
                      const iframeDoc = e.target.contentDocument || e.target.contentWindow.document;
                      const bodyElement = iframeDoc.body;
                      
                      if (bodyElement) {
                        // bodyのテキストコンテンツを取得（HTMLタグを除いた純粋なテキスト）
                        const textContent = bodyElement.textContent || bodyElement.innerText || '';
                        // 空白文字を除去して実際のコンテンツがあるかチェック
                        const hasContent = textContent.trim().length > 0;
                        
                        if (!hasContent) {
                          // テキストコンテンツが空の場合はエラーとして扱う
                          setIframeError(true);
                          setCurrentIframeSrc('');
                        } else {
                          setIframeError(false);
                        }
                      } else {
                        // bodyが存在しない場合もエラー
                        setIframeError(true);
                        setCurrentIframeSrc('');
                      }
                    } catch (error) {
                      // クロスオリジンエラーなど、アクセスできない場合は正常として扱う
                      console.warn('iframe content check failed (cross-origin?):', error);
                      setIframeError(false);
                    }
                  }}
                  onError={() => {
                    // 読み込みエラー時：即座にエラー表示
                    if (loadingTimeoutId) {
                      clearTimeout(loadingTimeoutId);
                      setLoadingTimeoutId(null);
                    }
                    setIframeError(true);
                    setCurrentIframeSrc('');
                  }}
                  style={{ border: "none" }}
                />
              ) : (
                <Flex
                  width="100%"
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <Heading as="h3" size="lg" mb={4} textAlign="center" color="gray.600">
                    準備中です
                  </Heading>
                  <Text fontSize="md" textAlign="center" color="gray.500">
                    書籍やWeb等でご確認ください
                  </Text>
                </Flex>
              )}
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GuideModal;
